import type {
  CSSProperties,
  KeyboardEvent as ReactKeyboardEvent,
  PointerEvent as ReactPointerEvent,
  RefObject,
  ReactNode,
} from 'react';
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useT } from '../../i18n';
import { pushOverlay, peekTop } from '../../commands/overlayStack';
import { IconButton } from './IconButton';
import { cx } from './classNames';
import styles from './Dialog.module.css';

let bodyLockCount = 0;
let previousBodyOverflow = '';

const SHEET_DEFAULT_VH = 60;
const SHEET_EXPANDED_VH = 92;
const SHEET_EXPAND_THRESHOLD_PX = 48;
const SHEET_CLOSE_THRESHOLD_PX = 96;

type SheetSnap = 'default' | 'expanded';

interface SheetDragState {
  pointerId: number;
  startY: number;
  startSnap: SheetSnap;
  lastDeltaY: number;
  target: HTMLElement;
  // Feinzeiger (Maus/Trackpad) verwenden kein Drag-Schliessen — nur Touch/Stift.
  pointerType: string;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function currentViewportHeight(): number {
  if (typeof window === 'undefined') return 0;
  return window.visualViewport?.height ?? window.innerHeight;
}

function isInteractiveTarget(target: EventTarget | null): boolean {
  // WICHTIG: Element statt HTMLElement — der Schließen-Button (X) enthält ein
  // SVG-Icon, dessen <svg>/<use> ein SVGElement ist (NICHT HTMLElement). Mit der
  // alten HTMLElement-Prüfung schlug der Check beim Tippen aufs Icon fehl, der
  // Sheet startete einen Drag (setPointerCapture) und fing den Close-Klick ab →
  // das Menü öffnete sich nach dem Schließen sofort wieder (Bug II). `.closest()`
  // funktioniert auf SVG- wie HTML-Elementen.
  if (!(target instanceof Element)) return false;
  return Boolean(target.closest('button, a, input, textarea, select, [role="button"], [data-sheet-drag-ignore]'));
}

function lockBodyScroll(): () => void {
  if (typeof document === 'undefined') return () => undefined;
  if (bodyLockCount === 0) {
    previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }
  bodyLockCount += 1;

  return () => {
    bodyLockCount = Math.max(0, bodyLockCount - 1);
    if (bodyLockCount === 0) {
      document.body.style.overflow = previousBodyOverflow;
    }
  };
}

// Fokus-Containment auf Dokumentebene: solange ein Dialog offen ist, wird der
// gesamte Hintergrund (alle direkten Kinder von <body> ausser dem Portal dieses
// Dialogs) per `inert` unfokussierbar + nicht-interaktiv gemacht. Das ergaenzt
// den manuellen Tab-Trap (handlePanelKeyDown) — mit `inert` im Hintergrund ist
// der Trap ohnehin nie mehr noetig, bleibt aber als Fallback erhalten.
//
// Referenzgezaehlt wie bodyLockCount (LIFO-fest fuer gestapelte Dialoge): pro
// Element merken wir den vorherigen inert-Zustand und ein Zaehler, wie viele
// offene Dialoge es inertisiert haben. Erst wenn der letzte Dialog schliesst,
// wird der Ausgangszustand wiederhergestellt. Body-Scroll-Lock bleibt davon
// unberuehrt (inert sperrt Fokus/Interaktion, NICHT Scrollen).
interface InertRecord {
  count: number;
  previousInert: boolean;
}

const inertRecords = new WeakMap<HTMLElement, InertRecord>();

// Gestapelte Dialoge (25.09.2026): Oeffneten zwei Dialoge im SELBEN Commit
// (Geschenk-Popup + Plus-Einstiegswahl), lief der inert-Effekt des ersten erst,
// als das Overlay des zweiten schon im DOM hing — beide inertisierten sich
// gegenseitig, nichts war mehr klickbar. Dialog-Overlays werden deshalb NIE
// ueber die Hintergrund-Referenzzaehlung gesperrt; stattdessen ist immer genau
// das oberste Overlay (Vordergrund-Ebene vor Standard, sonst letztes im DOM)
// interaktiv, alle darunter sind inert.
const DIALOG_OVERLAY_ATTR = 'data-dialog-overlay';

function syncDialogStack(closing: HTMLElement | null = null): void {
  if (typeof document === 'undefined') return;
  const overlays = Array.from(document.body.children).filter(
    (child): child is HTMLElement => child instanceof HTMLElement
      && child.hasAttribute(DIALOG_OVERLAY_ATTR)
      && child !== closing,
  );
  const foreground = overlays.filter((el) => el.getAttribute(DIALOG_OVERLAY_ATTR) === 'foreground');
  const top = (foreground.length ? foreground : overlays).at(-1) ?? null;
  for (const overlay of overlays) overlay.inert = overlay !== top;
}

function applyBackgroundInert(portalContainer: HTMLElement | null): () => void {
  if (typeof document === 'undefined' || !portalContainer) return () => undefined;

  const affected: HTMLElement[] = [];
  for (const child of Array.from(document.body.children)) {
    if (!(child instanceof HTMLElement)) continue;
    // Das eigene Portal (Overlay dieses Dialogs) NICHT inertisieren.
    if (child === portalContainer || child.contains(portalContainer)) continue;
    // Andere Dialog-Overlays regelt syncDialogStack (siehe oben).
    if (child.hasAttribute(DIALOG_OVERLAY_ATTR)) continue;

    const record = inertRecords.get(child);
    if (record) {
      record.count += 1;
    } else {
      inertRecords.set(child, { count: 1, previousInert: child.inert });
      child.inert = true;
    }
    affected.push(child);
  }

  syncDialogStack();

  return () => {
    syncDialogStack(portalContainer);
    for (const child of affected) {
      const record = inertRecords.get(child);
      if (!record) continue;
      record.count -= 1;
      if (record.count <= 0) {
        // Vorheriger Zustand wird exakt wiederhergestellt (LIFO): war das
        // Element schon vor dem Dialog inert, bleibt es inert.
        child.inert = record.previousInert;
        inertRecords.delete(child);
      }
    }
  };
}

function getFocusableElements(container: HTMLElement): HTMLElement[] {
  const selector = [
    'a[href]',
    'button:not([disabled])',
    'textarea:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  return Array.from(container.querySelectorAll<HTMLElement>(selector)).filter((element) => {
    if (element.hidden || element.getAttribute('aria-hidden') === 'true') return false;
    return element.tabIndex >= 0;
  });
}

export type DialogInitialFocus = RefObject<HTMLElement | null> | 'none';

function focusInitialElement(container: HTMLElement, initialFocus?: DialogInitialFocus): void {
  if (initialFocus === 'none') {
    container.focus({ preventScroll: true });
    return;
  }
  if (initialFocus?.current) {
    initialFocus.current.focus({ preventScroll: true });
    return;
  }
  const preferred = container.querySelector<HTMLElement>('[data-autofocus], [autofocus]');
  const target = preferred ?? getFocusableElements(container)[0] ?? container;
  target.focus({ preventScroll: true });
}

function restoreFocus(element: HTMLElement | null): void {
  if (!element || typeof document === 'undefined') return;
  if (!document.contains(element)) return;
  element.focus({ preventScroll: true });
}

export function useModalBackClose(open: boolean, onClose: () => void, closable = true): void {
  // Stable ref to the latest onClose so the registration effect does not
  // re-run on every parent render (each caller passes a fresh inline arrow).
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Stabile Identitaet je useId, damit dieser Eintrag im overlayStack eindeutig
  // per peekTop() adressierbar ist.
  const overlayId = useId();

  useEffect(() => {
    if (!open || !closable || typeof window === 'undefined') return undefined;

    // Einmal-Schutz: Hardware-Back und Escape sind zwei Trigger auf denselben
    // Closer. Ein zweiter Tastendruck vor dem Unmount darf onClose nicht erneut
    // feuern.
    let closed = false;
    const handler = () => {
      if (closed) return;
      closed = true;
      onCloseRef.current();
    };

    // Zentrale LIFO-Overlay-Verwaltung: legt diesen Dialog oben auf. Der
    // overlayStack dockt den Hardware-Back-Handler GENAU EINMAL fuer den ganzen
    // Stapel an — keine pro-Dialog-Bridge-Registrierung mehr.
    const entry = { id: overlayId, close: handler };
    const unregister = pushOverlay(entry);

    // Escape bleibt lokal verdrahtet (bubble-Phase), respektiert
    // defaultPrevented (Edit-Boxen/Inline-Felder duerfen Esc weiter abfangen).
    // Die „bin ich oberste Ebene?"-Pruefung kommt jetzt cross-type aus dem
    // overlayStack (peekTop) statt aus einem eigenen Dialog-Stapel.
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (event.defaultPrevented) return;
      if (peekTop() !== entry) return;
      event.preventDefault();
      handler();
    };
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('keydown', handleEscape);
      unregister();
    };
  }, [closable, open, overlayId]);
}

export interface DialogBaseProps {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  title?: string;
  footer?: ReactNode;
  closable?: boolean;
  mode: 'modal' | 'sheet' | 'sidesheet';
  initialSheetExpanded?: boolean;
  maxWidth?: number | string;
  /** Startfokus: Ref-Ziel, Dialog-Container ('none') oder bisheriger Auto-Default. */
  initialFocus?: DialogInitialFocus;
  /** Optionale Vordergrund-Ebene fuer Dialoge ueber blockierenden App-Gates. */
  layer?: 'default' | 'foreground';
}

export function DialogBase({
  open,
  onClose,
  children,
  title,
  footer,
  closable = true,
  mode,
  maxWidth,
  initialFocus,
  initialSheetExpanded = false,
  layer = 'default',
}: DialogBaseProps) {
  const { t } = useT();
  const titleId = useId();
  const overlayRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);
  const sheetDragRef = useRef<SheetDragState | null>(null);
  const isClosingRef = useRef(false);
  const [sheetSnap, setSheetSnap] = useState<SheetSnap>('default');
  const [sheetDragOffset, setSheetDragOffset] = useState(0);
  const [isSheetDragging, setIsSheetDragging] = useState(false);

  // Idempotenter Schliess-Trigger: dedupliziert mehrfach feuernde onClose
  // (X-Klick + Overlay + Drag + Hardware-Back + StrictMode-Doppelinvoke),
  // damit der open/doc-State nicht thrashen kann.
  const requestClose = useCallback(() => {
    if (isClosingRef.current) return;
    isClosingRef.current = true;
    onClose();
  }, [onClose]);

  useModalBackClose(open, requestClose, closable);

  // BEWUSST vor dem Body-Lock-/Fokus-Effekt: React ruft Cleanups in
  // Deklarations-Reihenfolge auf. So laeuft beim Schliessen ZUERST die
  // inert-Freigabe (Hintergrund wieder fokussierbar) und DANN restoreFocus() im
  // naechsten Effekt — sonst schluege das Zuruecksetzen des Fokus auf ein noch
  // inertes Element fehl.
  useEffect(() => {
    if (!open || typeof document === 'undefined') return undefined;
    // Hintergrund unfokussierbar machen (Fokus-Trap auf Dokumentebene). Laeuft
    // nach dem Commit, wenn overlayRef.current (das Portal-Overlay) gesetzt ist.
    const release = applyBackgroundInert(overlayRef.current);
    return release;
  }, [open]);

  useEffect(() => {
    if (!open || typeof document === 'undefined') return undefined;
    isClosingRef.current = false;
    restoreFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const unlock = lockBodyScroll();
    const focusTimer = window.setTimeout(() => {
      if (panelRef.current) focusInitialElement(panelRef.current, initialFocus);
    }, 0);

    return () => {
      window.clearTimeout(focusTimer);
      unlock();
      restoreFocus(restoreFocusRef.current);
    };
  }, [initialFocus, open]);

  useEffect(() => {
    if (mode !== 'sheet') return;
    sheetDragRef.current = null;
    setSheetSnap(initialSheetExpanded ? 'expanded' : 'default');
    setSheetDragOffset(0);
    setIsSheetDragging(false);
  }, [mode, open, initialSheetExpanded]);

  useEffect(() => {
    if (!open || mode !== 'sheet' || typeof window === 'undefined') return undefined;
    let focusTimer: number | null = null;
    const scrollFocusedField = () => {
      if (focusTimer !== null) window.clearTimeout(focusTimer);
      focusTimer = window.setTimeout(() => {
        focusTimer = null;
        const active = document.activeElement;
        if (!(active instanceof HTMLElement) || !panelRef.current?.contains(active)) return;
        if (!active.matches('input, textarea, select, [contenteditable="true"]')) return;
        active.scrollIntoView({ block: 'nearest', behavior: 'auto' });
      }, 50);
    };
    const viewport = window.visualViewport;
    viewport?.addEventListener('resize', scrollFocusedField);
    window.addEventListener('resize', scrollFocusedField);
    return () => {
      if (focusTimer !== null) window.clearTimeout(focusTimer);
      viewport?.removeEventListener('resize', scrollFocusedField);
      window.removeEventListener('resize', scrollFocusedField);
    };
  }, [mode, open]);

  if (!open || typeof document === 'undefined') return null;

  // Modus-spezifischer Default, wenn der Aufrufer nichts angibt: modal/sheet wie
  // bisher 520 (kein Regress), sidesheet schlanker (420) fuer Detail-/Kontextpanels.
  const effectiveMaxWidth = maxWidth ?? (mode === 'sidesheet' ? 420 : 520);
  const maxWidthValue = typeof effectiveMaxWidth === 'number' ? `${effectiveMaxWidth}px` : effectiveMaxWidth;
  const viewportHeight = currentViewportHeight();
  const sheetBaseVh = sheetSnap === 'expanded' ? SHEET_EXPANDED_VH : SHEET_DEFAULT_VH;
  const sheetDragVh = isSheetDragging && viewportHeight > 0
    ? clamp(sheetBaseVh - (sheetDragOffset / viewportHeight) * 100, SHEET_DEFAULT_VH, SHEET_EXPANDED_VH)
    : sheetBaseVh;
  const sheetConsumedDelta = viewportHeight > 0 ? ((sheetBaseVh - sheetDragVh) / 100) * viewportHeight : 0;
  const sheetTranslateY = isSheetDragging ? Math.max(0, sheetDragOffset - sheetConsumedDelta) : 0;
  const sheetStyle = {
    '--sheet-block-size': `${sheetDragVh}dvh`,
    '--sheet-translate-y': `${sheetTranslateY}px`,
  } as CSSProperties;
  // Q5 (E0/Paket G): maxWidth gilt in allen Modi. Im Sheet zusaetzlich
  // margin-inline:auto, damit ein begrenztes Sheet zentriert. Auf echten Handys
  // (<=440 px) ist der Default 520 > Viewport -> KEINE Wirkung (kein Handy-Regress).
  // Sidesheet: an inline-end angedockt (kein margin-auto), Breite ueber maxInlineSize,
  // volle Hoehe kommt aus der CSS (.sidesheetPanel). Kein sheetStyle -> kein Drag.
  let panelStyle: CSSProperties;
  if (mode === 'modal') {
    panelStyle = { maxWidth: maxWidthValue };
  } else if (mode === 'sidesheet') {
    panelStyle = { maxInlineSize: maxWidthValue };
  } else {
    panelStyle = { ...sheetStyle, maxInlineSize: maxWidthValue, marginInline: 'auto' };
  }
  const hasHeader = Boolean(title) || closable || mode === 'sheet';

  const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget && closable) requestClose();
  };

  const handlePanelKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Tab' || !panelRef.current) return;

    const focusable = getFocusableElements(panelRef.current);
    if (focusable.length === 0) {
      event.preventDefault();
      panelRef.current.focus({ preventScroll: true });
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;

    if (event.shiftKey && (active === first || !panelRef.current.contains(active))) {
      event.preventDefault();
      last.focus({ preventScroll: true });
      return;
    }

    if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus({ preventScroll: true });
    }
  };

  const startSheetDrag = (event: ReactPointerEvent<HTMLElement>) => {
    if (mode !== 'sheet' || event.button !== 0 || isInteractiveTarget(event.target)) return;
    // Maus/Trackpad: Drag-Geste deaktivieren. Das Sheet bleibt per X-Button,
    // Escape und Overlay-Klick schliessbar — ein kleiner versehentlicher Maus-Zug
    // darf das Sheet nicht unbeabsichtigt schliessen.
    if (event.pointerType === 'mouse') return;

    sheetDragRef.current = {
      pointerId: event.pointerId,
      startY: event.clientY,
      startSnap: sheetSnap,
      lastDeltaY: 0,
      target: event.currentTarget,
      pointerType: event.pointerType,
    };
    setIsSheetDragging(true);
    setSheetDragOffset(0);
    event.currentTarget.setPointerCapture(event.pointerId);
    event.preventDefault();
  };

  const moveSheetDrag = (event: ReactPointerEvent<HTMLElement>) => {
    const drag = sheetDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;

    const deltaY = event.clientY - drag.startY;
    drag.lastDeltaY = deltaY;
    setSheetDragOffset(deltaY);
    event.preventDefault();
  };

  const finishSheetDrag = (event: ReactPointerEvent<HTMLElement>) => {
    const drag = sheetDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;

    try {
      drag.target.releasePointerCapture(event.pointerId);
    } catch {
      // Pointer capture can already be gone after a cancel.
    }

    const deltaY = drag.lastDeltaY;
    const closeFromExpanded = drag.startSnap === 'expanded' && viewportHeight > 0 && deltaY > viewportHeight * 0.28;
    const shouldClose = closable && deltaY > SHEET_CLOSE_THRESHOLD_PX && (
      drag.startSnap === 'default' || closeFromExpanded
    );
    const shouldExpand = deltaY < -SHEET_EXPAND_THRESHOLD_PX;
    const shouldCollapse = drag.startSnap === 'expanded' && deltaY > SHEET_EXPAND_THRESHOLD_PX;

    sheetDragRef.current = null;
    setIsSheetDragging(false);
    setSheetDragOffset(0);

    if (shouldClose) {
      requestClose();
      return;
    }

    if (shouldExpand) {
      setSheetSnap('expanded');
      return;
    }

    if (shouldCollapse) {
      setSheetSnap('default');
      return;
    }

    setSheetSnap(drag.startSnap);
  };

  // Modus-spezifische Overlay-/Panel-Klassen. modal + sheet bleiben exakt wie
  // bisher; sidesheet ist rein additiv (eigene Klassen, kein Sheet-Verhalten).
  const overlayModeClass = mode === 'modal'
    ? styles.modalOverlay
    : mode === 'sidesheet'
      ? styles.sidesheetOverlay
      : styles.sheetOverlay;
  const panelModeClass = mode === 'modal'
    ? styles.modalPanel
    : mode === 'sidesheet'
      ? styles.sidesheetPanel
      : styles.sheetPanel;

  return createPortal(
    <div
      ref={overlayRef}
      className={cx(styles.overlay, overlayModeClass, layer === 'foreground' && styles.foregroundOverlay)}
      onMouseDown={handleOverlayClick}
      data-dialog-mode={mode}
      data-dialog-overlay={layer}
    >
      <div
        ref={panelRef}
        className={cx(
          styles.panel,
          panelModeClass,
          isSheetDragging && styles.sheetDragging,
        )}
        style={panelStyle}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        tabIndex={-1}
        onKeyDown={handlePanelKeyDown}
      >
        {mode === 'sheet' ? (
          <span
            className={styles.grabber}
            aria-hidden="true"
            onPointerDown={startSheetDrag}
            onPointerMove={moveSheetDrag}
            onPointerUp={finishSheetDrag}
            onPointerCancel={finishSheetDrag}
          />
        ) : null}
        {hasHeader ? (
          <header
            className={styles.header}
            onPointerDown={mode === 'sheet' ? startSheetDrag : undefined}
            onPointerMove={mode === 'sheet' ? moveSheetDrag : undefined}
            onPointerUp={mode === 'sheet' ? finishSheetDrag : undefined}
            onPointerCancel={mode === 'sheet' ? finishSheetDrag : undefined}
          >
            {title ? (
              <h2 className={styles.title} id={titleId}>
                {title}
              </h2>
            ) : (
              <span className={styles.title} aria-hidden="true" />
            )}
            {closable ? (
              <IconButton
                className={styles.closeButton}
                icon="close"
                aria-label={t('common.close')}
                onClick={requestClose}
                variant="ghost"
              />
            ) : null}
          </header>
        ) : null}
        <div className={styles.body}>{children}</div>
        {footer ? <footer className={styles.footer}>{footer}</footer> : null}
      </div>
    </div>,
    document.body,
  );
}
